import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import './TransferScreen.css';

interface TransferFormData {
  recipient: string;
  amount: string;
  fromWallet: 'main' | 'grant' | 'rewards';
  reference: string;
}

export const TransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TransferFormData>({
    recipient: '',
    amount: '',
    fromWallet: 'main',
    reference: ''
  });
  const [step, setStep] = useState<'form' | 'confirm' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mock wallet balances (in real app, this would come from context/API)
  const walletBalances = {
    main: 2500.00,
    grant: 750.00,
    rewards: 125.50
  };

  const handleInputChange = (field: keyof TransferFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.recipient.trim()) {
      setError('Please enter recipient phone number or wallet ID');
      return false;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    const amount = parseFloat(formData.amount);
    const availableBalance = walletBalances[formData.fromWallet];

    if (amount > availableBalance) {
      setError(`Insufficient funds. Available balance: R${availableBalance.toFixed(2)}`);
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      setStep('confirm');
    }
  };

  const handleConfirmTransfer = async () => {
    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, always succeed
      setStep('success');
    } catch (err) {
      setError('Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toFixed(2)}`;
  };

  const getWalletDisplayName = (wallet: string) => {
    return wallet.charAt(0).toUpperCase() + wallet.slice(1) + ' Wallet';
  };

  if (step === 'success') {
    return (
      <div className="transfer-screen">
        <div className="transfer-container">
          <div className="success-content">
            <div className="success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2>Transfer Successful!</h2>
            <div className="transfer-details">
              <p><strong>Amount:</strong> {formatCurrency(parseFloat(formData.amount))}</p>
              <p><strong>To:</strong> {formData.recipient}</p>
              <p><strong>From:</strong> {getWalletDisplayName(formData.fromWallet)}</p>
              {formData.reference && <p><strong>Reference:</strong> {formData.reference}</p>}
            </div>
            <div className="success-actions">
              <button 
                onClick={() => navigate('/dashboard')}
                className="primary-button"
              >
                Back to Dashboard
              </button>
              <button 
                onClick={() => {
                  setStep('form');
                  setFormData({
                    recipient: '',
                    amount: '',
                    fromWallet: 'main',
                    reference: ''
                  });
                }}
                className="secondary-button"
              >
                Make Another Transfer
              </button>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="transfer-screen">
        <div className="transfer-container">
          <div className="screen-header">
            <button onClick={() => setStep('form')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Confirm Transfer</h1>
          </div>

          <div className="confirmation-details">
            <div className="transfer-summary">
              <div className="amount-display">
                <span className="currency">R</span>
                <span className="amount">{parseFloat(formData.amount).toFixed(2)}</span>
              </div>
              
              <div className="transfer-info">
                <div className="info-row">
                  <span className="label">To:</span>
                  <span className="value">{formData.recipient}</span>
                </div>
                <div className="info-row">
                  <span className="label">From:</span>
                  <span className="value">{getWalletDisplayName(formData.fromWallet)}</span>
                </div>
                <div className="info-row">
                  <span className="label">Available Balance:</span>
                  <span className="value">{formatCurrency(walletBalances[formData.fromWallet])}</span>
                </div>
                {formData.reference && (
                  <div className="info-row">
                    <span className="label">Reference:</span>
                    <span className="value">{formData.reference}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="confirmation-actions">
            <button 
              onClick={handleConfirmTransfer}
              disabled={loading}
              className="primary-button"
            >
              {loading ? 'Processing...' : 'Confirm Transfer'}
            </button>
            <button 
              onClick={() => setStep('form')}
              disabled={loading}
              className="secondary-button"
            >
              Cancel
            </button>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="transfer-screen">
      <div className="transfer-container">
        <div className="screen-header">
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1>Send Money</h1>
        </div>

        <div className="transfer-form">
          <div className="form-section">
            <label htmlFor="recipient">Recipient</label>
            <input
              id="recipient"
              type="text"
              placeholder="Phone number or Wallet ID"
              value={formData.recipient}
              onChange={(e) => handleInputChange('recipient', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-section">
            <label htmlFor="fromWallet">From Wallet</label>
            <select
              id="fromWallet"
              value={formData.fromWallet}
              onChange={(e) => handleInputChange('fromWallet', e.target.value as 'main' | 'grant' | 'rewards')}
              className="form-select"
            >
              <option value="main">Main Wallet - {formatCurrency(walletBalances.main)}</option>
              <option value="grant">Grant Wallet - {formatCurrency(walletBalances.grant)}</option>
              <option value="rewards">Rewards Wallet - {formatCurrency(walletBalances.rewards)}</option>
            </select>
          </div>

          <div className="form-section">
            <label htmlFor="amount">Amount</label>
            <div className="amount-input-container">
              <span className="currency-symbol">R</span>
              <input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                className="amount-input"
              />
            </div>
            <div className="balance-info">
              Available: {formatCurrency(walletBalances[formData.fromWallet])}
            </div>
          </div>

          <div className="form-section">
            <label htmlFor="reference">Reference (Optional)</label>
            <input
              id="reference"
              type="text"
              placeholder="Payment reference"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              className="form-input"
              maxLength={50}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button 
              onClick={handleContinue}
              className="primary-button"
            >
              Continue
            </button>
          </div>

          <div className="alternative-methods">
            <div className="divider">
              <span>or</span>
            </div>
            <button 
              className="qr-scan-button"
              disabled
              title="QR scanning coming soon"
            >
              <i className="fas fa-qrcode"></i>
              Scan QR Code
            </button>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};